import { TimeOfDay } from "@/types/date"

import { assert, clamp } from "@/lib/utils/assertions"
import { findNearest } from "@/lib/utils/geo"

//
// Utilities for working with time lists and time pickers in the UI.
//

export type TimeList = TimeListEntry[]
export type TimeListEntry = { value: TimeOfDay }

export function buildTimeList(perHour = 4): TimeList {
  const timeList: TimeListEntry[] = []
  assert(60 % perHour === 0, "perHour must be a factor of 60")
  const minutesPerSegment = 60 / perHour
  for (let hour = 0; hour <= 23; hour++) {
    for (let segment = 0; segment <= perHour - 1; segment++) {
      const minute = segment * minutesPerSegment
      const value = { hour, minute }
      timeList.push({ value })
    }
  }
  return timeList
}

const diffTimesOfDay = (a: TimeOfDay, b: TimeOfDay): number =>
  Math.abs(a.hour * 60 + a.minute - (b.hour * 60 + b.minute))

export function findTimeForCoordinate(
  timeList: TimeList,
  height: number,
  currentPoint: number,
  startPoint?: number,
  startTime?: TimeOfDay
): TimeListEntry | undefined {
  startPoint ||= 0
  currentPoint = clamp(currentPoint, 0, height - 1)
  startPoint = clamp(startPoint, 0, height - 1)

  // Match startTime to the nearest list entry directly by TimeOfDay — don't
  // format it to a string and re-parse it (that round-trip is locale-fragile
  // and pointless when we already hold the value).
  const startIndex = startTime
    ? findNearest(timeList, (entry: TimeListEntry) =>
        diffTimesOfDay(entry.value, startTime)
      )
    : 0
  if (startIndex === -1) return

  let index: number
  if (currentPoint === startPoint) {
    return timeList[startIndex]
  } else if (currentPoint < startPoint) {
    // everthing between 0 and startPoint maps to the range [0, startIndex]
    const percent = Math.round((100 * currentPoint) / startPoint)
    index = Math.round((percent * (startIndex + 1)) / 100)
  } else {
    const percent = Math.round(
      (100 * (currentPoint - startPoint)) / (height - startPoint)
    )
    index =
      startIndex + Math.floor((percent * (timeList.length - startIndex)) / 100)
  }
  return timeList[index]
}

// For parsing USER INPUT, not for understanding data from our server.
export function parseTimeOfDay(value: string): TimeOfDay | undefined {
  value = value.toLowerCase().replace(/\s/g, "")

  if (value === "noon") {
    // TODO: localize
    return { hour: 12, minute: 0 }
  }

  // Turn "9" into "9:00"
  if (value.endsWith(":")) {
    value = value + "00"
  } else if (value.match(/^[1-9]$/) || value.match(/^1[012]$/)) {
    value = value + ":00"
  }

  // Turn "9am" into "9:00am"
  if (!value.includes(":") && value.match(/(am|pm) *$/)) {
    value = value.replace("am", ":00am").replace("pm", ":00pm")
  }

  const match = value.match(/^(\d\d?):(\d\d)\s*(am|pm)?$/)
  if (match) {
    let hour = parseInt(match[1])
    const minute = parseInt(match[2])
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      if (match[3]?.toLowerCase() === "pm") {
        if (hour >= 1 && hour < 12) {
          hour += 12
        }
      } else if (match[3]?.toLowerCase() === "am" && hour === 12) {
        // Only convert 12am to midnight, not bare "12:00" (which is 24h format)
        hour = 0
      }
      return { hour, minute }
    }
  }
}
